(() => {
  if (window.__blendStagedHeaderInitialized) return;
  window.__blendStagedHeaderInitialized = true;
  const SELECTOR = '[data-blend-staged-header]';
  const SCROLL_ENTER_THRESHOLD = 32;
  const SCROLL_EXIT_THRESHOLD = 8;
  const MENU_SAFE_ZONE_PADDING = 24;
  const mountedHeaders = new Map();

  const mountHeader = (header) => {
    if (mountedHeaders.has(header)) return;

    header.dataset.blendHeaderMounted = 'true';

    const controller = new AbortController();
    const { signal } = controller;
    let frameId = null;
    let safeZoneObserver;
    let safeZoneFrame = null;
    let isScrolled = window.scrollY > SCROLL_ENTER_THRESHOLD;
    const drawerContainer = header.querySelector('header-drawer > details');
    const drawerSummary = drawerContainer?.querySelector(':scope > summary');
    const drawerElement = drawerContainer?.closest('header-drawer');
    const desktopNavigation = header.querySelector('.header__inline-menu');
    const desktopMenus = [...header.querySelectorAll('.header__inline-menu .mega-menu')];
    const desktopSearch = header.querySelector('.blend-desktop-search');
    const desktopSearchInput = desktopSearch?.querySelector('input[type="search"]');

    const closeDesktopSearch = (clearSearchTerm = false) => {
      if (typeof desktopSearch?.close === 'function') desktopSearch.close(clearSearchTerm);
      if (clearSearchTerm && desktopSearchInput) {
        desktopSearchInput.value = '';
        if (typeof desktopSearch.toggleResetButton === 'function') desktopSearch.toggleResetButton();
      }
      desktopSearch?.removeAttribute('open');
      desktopSearchInput?.setAttribute('aria-expanded', 'false');
    };

    const updateState = () => {
      frameId = null;
      if (signal.aborted || !header.isConnected) return;
      const scrollPosition = Math.max(0, window.scrollY);

      if (!isScrolled && scrollPosition > SCROLL_ENTER_THRESHOLD) {
        isScrolled = true;
      } else if (isScrolled && scrollPosition < SCROLL_EXIT_THRESHOLD) {
        isScrolled = false;
      }

      const nextState = String(isScrolled);
      if (header.dataset.scrolled !== nextState) {
        header.dataset.scrolled = nextState;
      }
    };

    const onScroll = () => {
      if (frameId !== null) return;
      frameId = window.requestAnimationFrame(updateState);
    };

    updateState();
    window.addEventListener('scroll', onScroll, { passive: true, signal });

    if (desktopNavigation && desktopMenus.length > 0) {
      const hiddenDesktopNavigation = window.matchMedia('(max-width: 991px), (hover: none) and (pointer: coarse)');
      const menuParts = desktopMenus.map((menu) => ({
        menu,
        trigger: menu.querySelector(':scope > .header__menu-item'),
        panel: menu.querySelector(':scope > .mega-menu__content'),
      })).filter(({ trigger, panel }) => trigger && panel);
      let safeZonesDirty = true;
      const syncMenuSafeZones = () => {
        if (safeZoneFrame !== null) window.cancelAnimationFrame(safeZoneFrame);
        safeZoneFrame = null;
        if (signal.aborted || !header.isConnected || hiddenDesktopNavigation.matches || !safeZonesDirty) return;
        safeZonesDirty = false;
        const measurements = menuParts.map(({ menu, trigger, panel }) => {
          const safeTop = trigger.offsetTop + trigger.offsetHeight / 2;
          return { menu, values: {
            '--blend-menu-trigger-left': `${trigger.offsetLeft}px`,
            '--blend-menu-trigger-width': `${trigger.offsetWidth}px`,
            '--blend-menu-safe-top': `${safeTop}px`,
            '--blend-menu-safe-left': `${panel.offsetLeft - MENU_SAFE_ZONE_PADDING}px`,
            '--blend-menu-safe-width': `${panel.offsetWidth + MENU_SAFE_ZONE_PADDING * 2}px`,
            '--blend-menu-safe-height': `${Math.max(12, panel.offsetTop + 2 - safeTop)}px`,
          } };
        });
        measurements.forEach(({ menu, values }) => {
          Object.entries(values).forEach(([property, value]) => {
            if (menu.style.getPropertyValue(property) !== value) menu.style.setProperty(property, value);
          });
        });
      };
      const scheduleMenuSafeZones = () => {
        safeZonesDirty = true;
        if (hiddenDesktopNavigation.matches || safeZoneFrame !== null) return;
        safeZoneFrame = window.requestAnimationFrame(syncMenuSafeZones);
      };
      scheduleMenuSafeZones();
      document.fonts?.addEventListener('loadingdone', scheduleMenuSafeZones, { signal });
      hiddenDesktopNavigation.addEventListener('change', scheduleMenuSafeZones, { signal });

      if ('ResizeObserver' in window) {
        safeZoneObserver = new ResizeObserver(scheduleMenuSafeZones);
        safeZoneObserver.observe(desktopNavigation);
      } else {
        window.addEventListener('resize', scheduleMenuSafeZones, { signal });
      }

      desktopMenus.forEach((menu) => {
        const prepareMenu = () => {
          syncMenuSafeZones();
          closeDesktopSearch(true);
        };
        menu.addEventListener('pointerenter', prepareMenu, { signal });
        menu.addEventListener('focusin', prepareMenu, { signal });
      });
    }

    if (drawerContainer && drawerSummary) {
      const removeOutsidePointerListener = () => {
        document.removeEventListener('pointerdown', onOutsidePointerDown);
      };

      const closeDrawer = (event) => {
        removeOutsidePointerListener();

        if (typeof drawerElement?.closeMenuDrawer === 'function') {
          drawerElement.closeMenuDrawer(event, drawerSummary);
        } else {
          drawerSummary.click();
        }
      };

      function onOutsidePointerDown(event) {
        if (!drawerContainer.open || event.button !== 0 || drawerContainer.contains(event.target)) {
          return;
        }

        closeDrawer(event);
      }

      drawerContainer.addEventListener('toggle', () => {
        drawerSummary.setAttribute('aria-expanded', String(drawerContainer.open));

        if (drawerContainer.open) {
          document.addEventListener('pointerdown', onOutsidePointerDown, { signal });
        } else {
          removeOutsidePointerListener();
        }
      }, { signal });

      drawerContainer.addEventListener('click', (event) => {
        if (event.target !== drawerContainer || !drawerContainer.open) return;
        drawerSummary.click();
      }, { signal });
    }

    mountedHeaders.set(header, () => {
      controller.abort();
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      frameId = null;
      safeZoneObserver?.disconnect();
      if (safeZoneFrame !== null) window.cancelAnimationFrame(safeZoneFrame);
      safeZoneFrame = null;
      delete header.dataset.blendHeaderMounted;
      mountedHeaders.delete(header);
    });
  };

  const mountAllHeaders = (root = document) => {
    if (root.matches?.(SELECTOR)) mountHeader(root);
    root.querySelectorAll(SELECTOR).forEach(mountHeader);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mountAllHeaders(), { once: true });
  } else {
    mountAllHeaders();
  }

  document.addEventListener('shopify:section:load', (event) => mountAllHeaders(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    mountedHeaders.forEach((cleanup, header) => {
      if (event.target === header || event.target.contains(header)) cleanup();
    });
  });
})();
